/**
 * Tenant Context for Agency Dashboard
 *
 * Provides tenant configuration and branding throughout the agency application.
 * Used for pre-authentication branding (login/register pages).
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { environment } from '../env/environment';
import logger from '../utils/logger';

const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

// Default branding (fallback)
const DEFAULT_BRANDING = {
  id: null,
  name: 'Empower Care',
  brandName: 'Empower Care',
  slug: 'default',
  logoUrl: null,
  faviconUrl: null,
  primaryColor: '#06AB8B',
  secondaryColor: '#049572',
  features: {}
};

// Create context
const TenantContext = createContext({
  tenant: DEFAULT_BRANDING,
  loading: true,
  error: null,
  refetch: () => {},
});

/**
 * Extract tenant identifier from current URL
 */
const extractTenantFromUrl = () => {
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);

  // 1. Check query parameter first (?tenant=slug)
  const queryTenant = searchParams.get('tenant');
  if (queryTenant) {
    localStorage.setItem('agency_tenant_slug', queryTenant);
    return { type: 'slug', value: queryTenant };
  }

  // 2. For localhost WITHOUT ?tenant= parameter - use stored or default
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    const storedTenant = localStorage.getItem('agency_tenant_slug');
    if (storedTenant) {
      return { type: 'slug', value: storedTenant };
    }
    return null;
  }

  // 3. Agency app doesn't need tenant detection — it manages ALL tenants
  // No tenant slug or domain lookup needed for agency dashboard
  return null;
};

/**
 * Fetch tenant configuration from API
 */
const fetchTenantConfig = async (tenantInfo) => {
  try {
    let url;

    if (tenantInfo.type === 'slug') {
      url = `${environment.baseUrl}/api/tenants/slug/${tenantInfo.value}`;
    } else if (tenantInfo.type === 'domain') {
      url = `${environment.baseUrl}/api/tenants/domain/${tenantInfo.value}`;
    } else {
      return null;
    }

    const response = await fetch(url, {
      headers: {
        'X-Tenant-Domain': tenantInfo.type === 'domain' ? tenantInfo.value : '',
        'Cache-Control': 'no-cache',
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.warn('Tenant not found:', tenantInfo.value);
        return null;
      }
      throw new Error(`Failed to fetch tenant: ${response.status}`);
    }

    const data = await response.json();
    return data.tenant;
  } catch (error) {
    logger.error('Error fetching tenant config:', error);
    return null;
  }
};

/**
 * Transform API response to usable tenant object
 */
const transformTenant = (apiTenant) => {
  if (!apiTenant) return null;

  return {
    id: apiTenant.id,
    name: apiTenant.name,
    brandName: decodeHtmlEntities(apiTenant.brand_name || apiTenant.name),
    slug: apiTenant.slug,
    logoUrl: apiTenant.logo_url,
    signinLogoUrl: apiTenant.signin_logo_url,
    faviconUrl: apiTenant.favicon_url,
    primaryColor: apiTenant.primary_color || DEFAULT_BRANDING.primaryColor,
    secondaryColor: apiTenant.secondary_color || DEFAULT_BRANDING.secondaryColor,
    buttonHoverColor: apiTenant.button_hover_color,
    customDomain: apiTenant.custom_domain,
    features: apiTenant.features || {},
    settings: apiTenant.settings || {}
  };
};

/**
 * Apply tenant branding to document
 */
const applyBrandingToDocument = (tenant) => {
  // Set CSS variables for colors
  document.documentElement.style.setProperty('--color-primary', tenant.primaryColor);
  document.documentElement.style.setProperty('--color-secondary', tenant.secondaryColor);
  document.documentElement.style.setProperty('--color-primary-light', tenant.primaryColor + '20');

  // Button styling
  const hoverColor = tenant.buttonHoverColor || tenant.secondaryColor;
  document.documentElement.style.setProperty('--color-button-hover', hoverColor);

  // Set tenant ID on window for API access
  if (tenant.id) {
    window.__TENANT_ID__ = tenant.id;
    localStorage.setItem('agency_tenant_id', tenant.id);
    localStorage.setItem('agency_tenant_slug', tenant.slug);
  }

  // Remove ALL existing favicon links first
  const existingIcons = document.querySelectorAll("link[rel*='icon']");
  existingIcons.forEach(icon => icon.remove());

  // Set favicon with cache-busting
  if (tenant.faviconUrl) {
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = 'image/x-icon';
    faviconLink.href = tenant.faviconUrl + '?_t=' + Date.now();
    document.head.appendChild(faviconLink);
  }

  // Set page title
  if (tenant.brandName) {
    document.title = `${tenant.brandName} - Agency Dashboard`;
  }
};

/**
 * Tenant Provider Component
 */
export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if tenant was pre-fetched by the early detection script in index.html
      if (window.__TENANT_DATA__) {
        logger.log('[Agency TenantContext] Using pre-fetched tenant data:', window.__TENANT_DATA__.id);
        const transformedTenant = transformTenant(window.__TENANT_DATA__);

        if (transformedTenant) {
          setTenant(transformedTenant);
          applyBrandingToDocument(transformedTenant);
        }

        delete window.__TENANT_DATA__;
        return;
      }

      const tenantInfo = extractTenantFromUrl();

      if (!tenantInfo) {
        setTenant(DEFAULT_BRANDING);
        applyBrandingToDocument(DEFAULT_BRANDING);
        return;
      }

      const apiTenant = await fetchTenantConfig(tenantInfo);

      if (apiTenant) {
        const transformedTenant = transformTenant(apiTenant);
        setTenant(transformedTenant);
        applyBrandingToDocument(transformedTenant);
      } else {
        setTenant(DEFAULT_BRANDING);
        applyBrandingToDocument(DEFAULT_BRANDING);
      }
    } catch (err) {
      logger.error('Error loading tenant:', err);
      setError(err.message);
      setTenant(DEFAULT_BRANDING);
      applyBrandingToDocument(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const value = {
    tenant,
    loading,
    error,
    refetch: loadTenant,
    getLogo: () => tenant.logoUrl,
    getPrimaryColor: () => tenant.primaryColor,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to use tenant context
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;
